/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */

import React, { FC, useMemo } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import { useLocalStorage } from 'react-use';
import styled from 'styled-components';
import { Observer, promptName, useObserver } from '../../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../../Legacy';
import { Image } from '../../../../concepts/resources/Image';
import { ColorPreview } from '../../../../ui/ColorPreview';
import { SliderWithInput } from '../../../../ui/SliderWithInput';
import { Tip } from '../../../../ui/Tip';
import { Emoji, EmojiPicker } from '../../../../ui/emoji';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { PopoverContext } from '../../ui/Popover';
import { PopperToggle } from '../../ui/PopperElements';
import { ActionItem, ActionLayout, Box, ColorSelector, Colors, Float, Menu, Prompt, remInPx, remToPx, toRem } from './../../../../ui';
import {
    ButtonStripWidget, ButtonWidget,
    ColorSelectorWidget,
    DivideWidget,
    EmojiSelectorWidget, InlineWidget,
    MenuWidget,
    PopoutWidget,
    SliderWidget,
    TextInputWidget,
    Widget,
    WidgetContainer,
    WidgetContainerPosition
} from './WidgetInterface';

type MountFor<T, U = unknown> = FC<{ container: WidgetContainer, widget: T } & U>;

// const modifiers = [
//     {
//         name: 'preventOverflow',
//         options: {
//             padding: 80,
//         },
//     },
// ];

const id = (container: WidgetContainer, widget: Widget) => ({ id: `${container.name}-${widget.name}` });

// WidgetContainerPosition -> PopperPosition
const wcp2pp = (wcp: WidgetContainerPosition): [string, any] => {
    const sd = [0, remInPx(.5)];
    if (wcp[0] === 'docked') {
        switch (wcp[1]) {
            case 'topLeft': return ['right-start', sd];
            case 'top': return ['bottom', sd];
            case 'topRight': return ['bottom-end', sd];
            case 'right': return ['left', sd];
            case 'bottomRight': return ['top-end', sd];
            case 'bottom': return ['top', [sd]];
            case 'bottomLeft': return ['top', sd];
            case 'left': return ['right', sd];
            default: return ['auto', 0];
        }
    }
    if (wcp[0] === 'attached') {
        return ['top', sd];
    }
    return ['auto', sd];
};

// WidgetContainerPosition -> TooltipPosition
const wcp2tp = (input: WidgetContainer | WidgetContainerPosition): any => {
    const [mode, val] = ('position' in input ? input.position : input) ?? [];
    if (mode === 'docked') {
        switch (val) {
            case 'topLeft': return 'right';
            case 'top': return 'bottom';
            case 'topRight': return 'left';
            case 'right': return 'left';
            case 'bottomRight': return 'top';
            case 'bottom': return 'top';
            case 'bottomLeft': return 'top';
            case 'left': return 'right';
            default: return 'auto';
        }
    }
    if (mode === 'attached') {
        return 'top';
    }
    return 'auto';
};

const popperProps = (container: WidgetContainer, placement?: string) => {

    const auto = wcp2pp(container.position);

    placement = placement ?? auto[0];
    const offset = auto[1];

    const modifiers = [
        {
            name: 'offset',
            options: { offset },
        },
        {
            name: 'preventOverflow',
            options: {
                boundary: document.getElementById('canvas'),
                padding: {
                    top: remInPx(2),
                    bottom: remInPx(5),
                    left: remInPx(1),
                    right: remInPx(1),
                },
            },
        }
    ];

    return { modifiers, placement } as any;
};

// const =.update({ value: sw }: Widget<WidgetBaseProps & SelectorWidgetProps<any>>, value: any) => {
//     sw.update({ value });
//     sw.onSelect();
// };

// const onPopperWidgetToggle = (pw: Widget<WidgetBaseProps & PopperWidgetProps>, force?: boolean) => {
//     const open = force ?? !pw.open;
//     if (pw.open === open) return;
//     pw.update({ open });
//     if (open) pw.onOpen();
//     else pw.onClose();
// };

//
// Shared
//

const PreRender = () => {

};

const WidgetIcon: LFC<{ src: string, scale: number }> = ({ src, scale }) => {
    const size = remToPx(1.25 + (.25 * scale));
    const style = { width: size, height: size };
    return <Image style={style} src={src} />;
};

const buttonLikeStyling = (width?: any, align?: string): any => {
    const styling = {} as any;
    if (align === 'left') styling.justifyContent = 'start';
    if (align === 'center') styling.justifyContent = 'center';
    if (align === 'right') styling.justifyContent = 'end';
    if (width) styling.width = toRem(width);
    return styling;
};

//
// ButtonWidget
//

const buttonAlignStyling = (align?: string): any => {
    if (align === 'left') return { justifyContent: 'start' };
    if (align === 'center') return { justifyContent: 'center' };
    if (align === 'right') return { justifyContent: 'end' };
    return {};
};

const ButtonWidgetMount: MountFor<ButtonWidget, { align?: string }> = ({ container, widget, align }) =>
    <Observer store={widget} render={() =>
        <Tip text={widget.text ?? ''} position={wcp2tp(container)} disabled={!widget.image} >
            <ActionItem role="button" aria-label={widget.text} {...id(container, widget)} style={buttonAlignStyling(align)} pressable selected={!!widget.active} onClick={() => widget.onAction()}>
                {widget.image ? <WidgetIcon scale={container.scale} src={widget.image} /> : <span>{widget.text}</span>}
            </ActionItem>
        </Tip>
    } />;

//
// ColorSelectorWidget
//

const ColorSelectorWidgetMount: MountFor<ColorSelectorWidget> = ({ container, widget }) => {
    const pn = promptName();
    const [_lastColor, setLastColor] = useLocalStorage('deqo/ui/colorSelector/last');
    return <Observer store={widget} render={() =>
        <Manager>
            <Reference>
                {({ ref }) =>
                    <Tip disabled={widget.open} text={widget.text ?? ''} position={wcp2tp(container)}>
                        <ActionItem {...id(container, widget)} className={`ignore-${pn}`} ref={ref} pressable selected={!!widget.active} onClick={() => widget.update({ open: !widget.open })}>
                            {widget.image && <WidgetIcon scale={container.scale} src={widget.image} />}
                            {!widget.image && <ColorPreview scale={container.scale} color={widget.value} />}
                        </ActionItem>
                    </Tip>
                }
            </Reference>
            {widget.open && <Popper {...popperProps(container)}>
                {({ ref, style, placement, arrowProps }) => (
                    <div ref={ref} style={style} data-placement={placement}>
                        <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => widget.update({ open: false })}>
                            <Float shadow="lg" inline>
                                <ColorSelector allowNull={widget.allowNull} autoSelect={widget.autoSelect} groups={widget.groups} color={widget.value} onSelect={(color, complete) => {
                                    widget.update({ value: color, open: !complete });
                                    setLastColor(color);
                                }} />
                            </Float>
                        </Prompt>
                        {/* <Float.Arrow ref={arrowProps.ref} style={arrowProps.style} data-placement={placement} />*/}
                    </div>
                )}
            </Popper>}
        </Manager>
    } />;
};

//
// DivideWidget
//

const DivideWidgetMount: MountFor<DivideWidget> = ({ container, widget }) => {

    useObserver(widget);

    return <Flexer grow margin=".375" width=".0625" background={Colors.border} />;
};

//
// EmojiSelectorWidget
//

const EmojiFrame = styled.div`
        font-size: 1.5rem;
        width: 1.5rem;
        height: 1.5rem;
        box-sizing: border-box;
    `;

const EmojiPreview: LFC<{ idOrCode: string | null }> = ({ idOrCode }) => {
    return <EmojiFrame>
        {idOrCode && <Emoji idOrCode={idOrCode} />}
    </EmojiFrame>;
};

const EmojiSelectorWidgetMount: MountFor<EmojiSelectorWidget> = ({ container, widget }) => {
    const pn = promptName();
    return <Observer store={widget} render={() =>
        <Manager>
            <Reference>
                {({ ref }) =>
                    <Tip disabled={widget.open} text={widget.text ?? ''} position={wcp2tp(container)}>
                        <ActionItem {...id(container, widget)} ref={ref} className={`ignore-${pn}`} pressable selected={!!widget.active} onClick={() => widget.update({ open: !widget.open })}>
                            {widget.image ? <WidgetIcon scale={container.scale} src={widget.image} /> : <EmojiPreview idOrCode={widget.value ?? null} />}
                        </ActionItem>
                    </Tip>
                }
            </Reference>
            {widget.open && <Popper {...popperProps(container)}>
                {({ ref, style, placement, arrowProps }) => (
                    <div ref={ref} className="blockWheel" style={style} data-placement={placement} /* direction="column" minWidth={120} padding="0.5rem" textAlign="left" layer={10}*/>
                        <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => widget.update({ open: false })}>
                            <Float shadow="lg" inline>
                                <EmojiPicker onSelect={e => {
                                    widget.update({ value: e?.id ?? null });
                                    widget.update({ open: false });
                                }} />
                            </Float>
                        </Prompt>
                        {/* <Float.Arrow ref={arrowProps.ref} style={arrowProps.style} data-placement={placement} />*/}
                    </div>
                )}
            </Popper>}
        </Manager>
    } />;
};

//
// ButtonStripWidget
//

const ButtonStripWidgetMount: MountFor<ButtonStripWidget> = ({ container, widget }) => {
    const pn = promptName();
    return <Observer store={widget} render={() =>
        <Manager>
            <Reference>
                {({ ref }) =>
                    <Tip disabled={widget.open || !widget.image} text={widget.text ?? ''} position={wcp2tp(container)}>
                        <ActionItem {...id(container, widget)} className={`ignore-${pn}`} ref={ref} pressable onClick={() => widget.update({ open: !widget.open })} >
                            {widget.image ? <WidgetIcon scale={container.scale} src={widget.image} /> : <span>{widget.text}</span>}
                        </ActionItem>
                    </Tip>
                }
            </Reference>
            {widget.open && <Popper {...popperProps(container)}>
                {({ ref, style, placement, arrowProps, hasPopperEscaped }) => {

                    return <div ref={ref} className="blockWheel" style={{ ...style, zIndex: 10 }} data-placement={placement}>
                        <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => widget.update({ open: false })}>
                            <Float shadow="lg" inline>
                                <Box>
                                    <ActionLayout mode={widget.layout} padding={1} lines={container.style === 'floatWithLines'}>
                                        {widget.items.map(x => <ButtonWidgetMount key={x.id} container={container} widget={x} align="left" />)}
                                    </ActionLayout>
                                </Box>
                            </Float>
                        </Prompt>
                        {/* <Float.Arrow ref={arrowProps.ref} style={arrowProps.style} data-placement={placement} />*/}
                    </div>;
                }}
            </Popper>}
        </Manager>
    } />;
};

//
// InlineWidget
//

const InlineWidgetMount: MountFor<InlineWidget> = ({ container, widget }) =>
    <Observer store={widget} render={() =>
        // <ActionItem title={widget.text ?? ''}>
        <span>
            {React.createElement(widget.ui, { widget })}
        </span>
    } />;

//
// MenuWidget
//

// const styling = { background: 'red', width: !!widget.width ? toRem(widget.width) : '', ...buttonAlignStyling('center') };
//     return <Tip text={widget.text ?? ''} position={wcp2tp(container)}>
//         <ActionItem ref={ref} className={`ignore-${pn}`} style={styling} pressable selected={!!widget.active} onClick={() => widget.update({ open: !widget.open })}>
//             {widget.image ? <WidgetIcon scale={container.scale} src={widget.image} /> : <EmojiPreview idOrCode={widget.value ?? null} />}
//         </ActionItem>
//     </Tip>;

const MenuWidgetMount: MountFor<MenuWidget> = ({ container, widget }) => {
    const pn = promptName();
    return <Observer store={widget} render={() =>
        <Manager>
            <Reference>
                {({ ref }) =>
                    <Tip disabled={widget.open} text={widget.text ?? ''} position={wcp2tp(container)}>
                        <ActionItem {...id(container, widget)} ref={ref} className={`ignore-${pn}`} style={buttonLikeStyling(widget.width)} pressable selected={!!widget.active} onClick={() => widget.update({ open: !widget.open })} >
                            {widget.image ? <WidgetIcon scale={container.scale} src={widget.image} /> : <span>{widget.text}</span>}
                        </ActionItem>
                    </Tip>
                }
            </Reference>
            {widget.open && <Popper {...popperProps(container)}>
                {({ ref, style, placement, arrowProps, hasPopperEscaped }) => {
                    return <div ref={ref} style={{ ...style, zIndex: 10 }} data-placement={placement}>
                        <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => widget.update({ open: false })}>
                            <Float shadow="lg" inline>
                                <Menu>
                                    {widget.items.map(x =>
                                        x.text === '-' ? <Menu.Divide key={x.id} /> :
                                            <Menu.Item
                                                key={x.id}
                                                checked={x.active}
                                                style={{ minWidth: toRem(widget.itemsWidth || 0) }}
                                                onClick={() => x.onAction()}>
                                                <Menu.ItemText>{x.text}</Menu.ItemText>
                                            </Menu.Item>
                                    )}
                                </Menu>
                            </Float>
                        </Prompt>
                        {/* <Float.Arrow ref={arrowProps.ref} style={arrowProps.style} data-placement={placement} />*/}
                    </div>;
                }}
            </Popper>}
        </Manager>
    } />;
};

//
// PopoutWidget
//

const PopoutWidgetMount: MountFor<PopoutWidget> = ({ container, widget }) => {
    const pn = useMemo(() => promptName(), []);
    return <PopoverContext.Provider value={{ layers: [pn] }}>
        <Observer store={widget} render={() =>
            <Manager>
                <Reference>
                    {({ ref }) =>
                        <Tip disabled={widget.open} text={widget.text ?? ''} position={wcp2tp(container)}>
                            <ActionItem {...id(container, widget)} className={`ignore-${pn}`} data-tag={widget.tag} ref={ref} pressable selected={!!widget.open || !!widget.active} onClick={() => widget.update({ open: !widget.open })}>
                                {widget.image ? <WidgetIcon scale={container.scale} src={widget.image} /> : <span>{widget.text}</span>}
                            </ActionItem>
                        </Tip>
                    }
                </Reference>
                <PopperToggle open={widget.open}>
                    <Popper {...popperProps(container, widget.placement)}>
                        {({ ref, style, placement, arrowProps }) => (
                            <div ref={ref} className="blockWheel" style={{ ...style, zIndex: 50 }} data-placement={placement} /* direction="column" minWidth={120} padding="0.5rem" textAlign="left" layer={10}*/>
                                <Prompt name={pn} ignoreMouse={!widget.autoClose} ignoreEnter onAction={() => widget.update({ open: false })}>
                                    <Float shadow="lg" inline>
                                        {React.createElement(widget.ui, { widget })}
                                    </Float>
                                    {/* <Float.Arrow ref={arrowProps.ref} style={arrowProps.style} data-placement={placement} />*/}
                                </Prompt>
                            </div>
                        )}
                    </Popper>
                </PopperToggle>
            </Manager>
        } />
    </PopoverContext.Provider>;
};

//
// SliderWidget
//

const SliderWidgetMount: MountFor<SliderWidget> = ({ container, widget }) => {
    const pn = promptName();
    return <Observer store={widget} render={() =>
        <Manager>
            <Reference>
                {({ ref }) =>
                    <Tip disabled={widget.open} text={widget.text ?? ''} position={wcp2tp(container)}>
                        <ActionItem {...id(container, widget)} className={`ignore-${pn}`} ref={ref} pressable selected={!!widget.active} onClick={() => widget.update({ open: !widget.open })} >
                            {widget.image ? <WidgetIcon scale={container.scale} src={widget.image} /> : <span>{widget.text}</span>}
                        </ActionItem>
                    </Tip>
                }
            </Reference>
            {widget.open && <Popper {...popperProps(container)}>
                {({ ref, style, placement, arrowProps }) => (
                    <div ref={ref} style={style} data-placement={placement} /* direction="column" minWidth={120} padding="0.5rem" textAlign="left" layer={10}*/>
                        <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => widget.update({ open: false })}>
                            <Float shadow="lg" inline>
                                <Box padding=".5" width="15">
                                    <SliderWithInput
                                        min={widget.min}
                                        max={widget.max}
                                        value={widget.value}
                                        onChange={(value: number) => widget.update({ value })}
                                    />
                                </Box>
                            </Float>
                            {/* <Float.Arrow ref={arrowProps.ref} style={arrowProps.style} data-placement={placement} />*/}
                        </Prompt>
                    </div>
                )}
            </Popper>}
        </Manager>
    } />;
};

//
// SliderWidget
//

const TextInputWidgetMount: MountFor<TextInputWidget> = ({ container, widget }) =>
    <Observer store={widget} render={() =>
        <ActionItem {...id(container, widget)}>
            <input type="text" value={widget.value} onChange={e => widget.update({ value: e.target.value })} />
        </ActionItem>
    } />;


export const WidgetMount: LFC<{ container: WidgetContainer, widget: any }> = props => {
    switch (props.widget.type) {
        case 'button': return <ButtonWidgetMount {...props} />;
        case 'buttonStrip': return <ButtonStripWidgetMount {...props} />;
        case 'colorSelector': return <ColorSelectorWidgetMount {...props} />;
        case 'divide': return <DivideWidgetMount {...props} />;
        case 'emojiSelector': return <EmojiSelectorWidgetMount {...props} />;
        case 'inline': return <InlineWidgetMount {...props} />;
        case 'menu': return <MenuWidgetMount {...props} />;
        case 'popout': return <PopoutWidgetMount {...props} />;
        case 'slider': return <SliderWidgetMount {...props} />;
        case 'textInput': return <TextInputWidgetMount {...props} />;
        default: throw new Error(`Unsupported widget type: ${props.widget.type}`);
    }
};
